import React, { useState, useEffect, useRef, useCallback } from 'react';
import { autoplannerAPI } from '../../../services/api';
import ProcessingStatus from './ProcessingStatus';
import StepNavigation from './StepNavigation';
import { ClockIcon } from '@heroicons/react/24/outline';

const RegisterNavUpdater = ({ registerNav, onNext, onBack, canNext, canBack }) => {
  const prevRef = useRef(null);
  const onNextRef = useRef(onNext);
  const onBackRef = useRef(onBack);

  // Update refs when props change
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  // Only register when the nav config actually changes in value, not just identity
  useEffect(() => {
    const payload = {
      onNext: onNextRef.current,
      onBack: onBackRef.current,
      canNext,
      canBack,
    };
    const prev = prevRef.current;
    const changed =
      !prev ||
      prev.onNext !== payload.onNext ||
      prev.onBack !== payload.onBack ||
      prev.canNext !== payload.canNext ||
      prev.canBack !== payload.canBack;
    if (changed) {
      registerNav(payload);
      prevRef.current = payload;
    }
  }, [registerNav, canNext, canBack]);
  return null;
};
import ConfirmationSummary from './ConfirmationSummary';

const Step7Processing = ({ taskId, onComplete, formData, trainingGroups, registerNav }) => {
  const [taskStatus, setTaskStatus] = useState('processing');
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Initializing task...');
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [normalizedData, setNormalizedData] = useState(null);
  const [loadingNormalizedData, setLoadingNormalizedData] = useState(false);
  const initialStatusRef = useRef(null);
  const isActiveRef = useRef(true);
  const pollTimeoutRef = useRef(null);
  const onCompleteRef = useRef(onComplete);

  // Update ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Load normalized dataset to get excluded rowers per session
  const loadNormalizedData = async () => {
    if (!taskId) return;

    try {
      setLoadingNormalizedData(true);
      const result = await autoplannerAPI.getNormalizedDataset(taskId);
      if (result.success && result.normalized_data?.success) {
        setNormalizedData(result.normalized_data.normalized_data);
      }
    } catch (error) {
      console.error('Error loading normalized dataset:', error);
    } finally {
      setLoadingNormalizedData(false);
    }
  };

  useEffect(() => {
    loadNormalizedData();
  }, [taskId]);

  // Extract excluded rowers per session from normalized data
  const excludedRowersPerSession = normalizedData?.display_data?.excluded_rowers_per_session || {};

  useEffect(() => {
    isActiveRef.current = true;
    if (taskId) pollTaskStatus();

    return () => {
      // mark inactive and clear any pending timers
      isActiveRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [taskId]);

  const pollTaskStatus = async () => {
    try {
      const task = await autoplannerAPI.getAutoplannerTask(taskId);
      if (!isActiveRef.current) return; // ignore if unmounted

      // Capture initial status on first poll BEFORE branching logic
      if (initialStatusRef.current === null) {
        initialStatusRef.current = task.status;
      }

      if (task.status === 'completed') {
        setTaskStatus('completed');
        setProgress(100);
        setStatusMessage('Task completed successfully!');

        // Only auto-redirect if the task wasn't completed when we first landed on this page
        if (initialStatusRef.current !== 'completed') {
          if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = setTimeout(() => {
            if (isActiveRef.current) onCompleteRef.current(true); // auto-show results when finishing now
          }, 2000);
        } else if (pollTimeoutRef.current) {
          // Ensure we don't have a stray timer if viewing an already-completed task
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
      } else if (task.status === 'failed') {
        setTaskStatus('failed');
        setError('The autoplanner task failed to process. Please try again.');
      } else if (task.status === 'processing_algorithm') {
        setProgress((prev) => Math.min(prev + 10, 90));
        setStatusMessage('Processing with AI...');
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = setTimeout(() => {
          if (isActiveRef.current) pollTaskStatus();
        }, 5000);
      } else if (task.status === 'pending_for_algorithm') {
        setProgress(10);
        setStatusMessage('Waiting for algorithm to start...');
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = setTimeout(() => {
          if (isActiveRef.current) pollTaskStatus();
        }, 5000);
      }
    } catch (error) {
      console.error('Error polling task status:', error);
      if (isActiveRef.current) setError('Error checking task status. Please refresh the page.');
    }
  };

  const handleRetry = async () => {
    if (!taskId) return;
    try {
      setRetrying(true);
      setError(null);
      setTaskStatus('pending_for_algorithm');
      setProgress(10);
      setStatusMessage('Retrying... preparing data');

      // Ensure normalized dataset exists before re-dispatching
      try {
        await autoplannerAPI.createNormalizedDataset(taskId);
        // Reload normalized data to get updated excluded rowers
        await loadNormalizedData();
      } catch (e) {
        // Non-fatal; continue to dispatch
        console.warn('Retry: createNormalizedDataset failed, continuing to dispatch', e);
      }

      // Set task back to dispatch to re-queue processing
      await autoplannerAPI.updateAutoplannerTask(taskId, {}, 'dispatch');

      setStatusMessage('Waiting for algorithm to start...');
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = setTimeout(() => {
        if (isActiveRef.current) pollTaskStatus();
      }, 1000);
    } catch (e) {
      console.error('Error retrying autoplanner task:', e);
      if (isActiveRef.current) {
        setError('Retry failed. Please try again later.');
        setTaskStatus('failed');
      }
    } finally {
      if (isActiveRef.current) setRetrying(false);
    }
  };

  const getProgressColor = () => {
    switch (taskStatus) {
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-blue-500';
    }
  };

  const onNextCb = useCallback(
    () => (taskStatus === 'completed' ? onCompleteRef.current(true) : onCompleteRef.current(false)),
    [taskStatus]
  );
  const onBackCb = useCallback(() => onCompleteRef.current(false), []);

  return (
    <div className="space-y-6">
      {typeof registerNav === 'function' && (
        <RegisterNavUpdater
          registerNav={registerNav}
          onNext={onNextCb}
          onBack={onBackCb}
          canNext={true}
          canBack={true}
        />
      )}
      {/* Progress Bar at the top */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="space-y-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Progress</span>
            <span className="text-gray-900">{progress}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${getProgressColor()}`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      <ProcessingStatus taskStatus={taskStatus} statusMessage={statusMessage} />

      {/* Configuration Overview */}
      {formData && trainingGroups && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="mb-4 text-sm font-medium text-gray-700">Task Configuration Overview</h3>
          <div className="px-2 sm:px-0">
            <ConfirmationSummary
              formData={formData}
              trainingGroups={trainingGroups}
              taskId={taskId}
            />
          </div>
        </div>
      )}

      {/* Excluded Rowers Per Session Summary */}
      {!loadingNormalizedData && Object.keys(excludedRowersPerSession).length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ClockIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Excluded Rowers Per Session</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p className="mb-3">
                  The following rowers have been excluded from specific sessions:
                </p>
                <div className="space-y-2">
                  {Object.entries(excludedRowersPerSession).map(([sessionId, exclusions]) => {
                    if (!Array.isArray(exclusions) || exclusions.length === 0) return null;

                    // Get session details from normalized data
                    const sessionDetails = normalizedData?.display_data?.session_details?.find(
                      (session) => session.id.toString() === sessionId
                    );
                    const sessionTitle = sessionDetails?.title || `Session ${sessionId}`;
                    const sessionDate = sessionDetails?.date;

                    return (
                      <div
                        key={sessionId}
                        className="rounded border border-yellow-300 bg-yellow-100 p-2"
                      >
                        <p className="mb-1 text-xs font-medium text-yellow-800">
                          {sessionTitle} {sessionDate && `(${sessionDate})`}:
                        </p>
                        <ul className="space-y-1 text-xs text-yellow-700">
                          {exclusions.map((exclusion, index) => {
                            // Get user details from normalized data
                            const userDetails =
                              normalizedData?.display_data?.user_details?.[exclusion.user_id];
                            const userName = userDetails?.name || `User ${exclusion.user_id}`;

                            return (
                              <li key={index} className="flex items-center justify-between">
                                <span>{userName}</span>
                                {exclusion.reason && (
                                  <span className="text-yellow-600">({exclusion.reason})</span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      <div className="flex justify-center">
        {taskStatus === 'completed' ? (
          <button
            onClick={() => onCompleteRef.current(true)}
            className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
          >
            View Results
          </button>
        ) : taskStatus === 'failed' ? (
          <button
            onClick={handleRetry}
            disabled={retrying}
            className={`inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm ${retrying ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
          >
            {retrying ? 'Retrying…' : 'Try Again'}
          </button>
        ) : (
          <button
            onClick={() => onCompleteRef.current(false)}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Return to Dashboard
          </button>
        )}
      </div>
    </div>
  );
};

export default Step7Processing;
